import { ListingType, PriceByGuest } from "~graphql/generated/graphql"
import type {
    QuoteFieldsFragment,
    PricingPackageFieldsFragment,
    QuotePackageFieldsFragment,
    BookingInviteFieldsFragment,
    BookingInvitePackageFieldsFragment,
    BookingInviteEditableFieldsFragment,
} from "~graphql/generated/graphql"
import type {
    BookingInvitePackageInfo,
    PackageState,
    StepConfig,
    BookingInviteEditableFields,
    GetStepConfigsFuncArgs,
} from "~components/pages/listing/checkout/checkout-flow/checkout-flow-types"
import { CHECKOUT_STEPS } from "~config/constants"
import { getQuotePackageIdUrlQueryParam } from "~utils/url-param-helpers"
import COPY from "~config/copy-constants"
import type { CheckoutState } from "~utils/use-checkout-query-params"
import { mapAddOnToAddOnInput } from "~components/pages/listing/checkout/add-ons/extra-add-ons-helpers"

export function getInitialCheckoutFlowState({
    pricingPackage = null,
    bookingInvitePackageInfo,
    quotePackageBeingEdited,
    quote,
    persistedCheckoutState,
}: {
    pricingPackage?: PricingPackageFieldsFragment | null
    bookingInvitePackageInfo?: BookingInvitePackageInfo | null
    quotePackageBeingEdited?: QuotePackageFieldsFragment | null
    quote?: QuoteFieldsFragment | null
    persistedCheckoutState: CheckoutState | null
}) {
    const initialDates = getInitialDates({
        bookingInvitePackageInfo,
        quote,
        persistedCheckoutState,
    })
    const initialPackageState = getDefaultPackageState({
        pricingPackage,
        bookingInvitePackageInfo,
        quotePackageBeingEdited,
        persistedCheckoutState,
    })

    return { initialDates, initialPackageState }
}

export function getDefaultPackageState({
    pricingPackage = null,
    bookingInvitePackageInfo,
    quotePackageBeingEdited,
    persistedCheckoutState,
}: {
    pricingPackage?: PricingPackageFieldsFragment | null
    bookingInvitePackageInfo?: BookingInvitePackageInfo | null
    quotePackageBeingEdited?: QuotePackageFieldsFragment | null
    persistedCheckoutState?: CheckoutState | null
} = {}): PackageState {
    const initialNumberOfGuests = getInitialNumberOfGuests({
        pricingPackage,
        bookingInvitePackageInfo,
        quotePackageBeingEdited,
        persistedCheckoutState,
    })
    const initialPricingPackageId = getInitialPricingPackageId({
        pricingPackage,
        bookingInvitePackageInfo,
        quotePackageBeingEdited,
        persistedCheckoutState,
    })
    const initialAddOns = getInitialAddOns({
        bookingInvitePackageInfo,
        quotePackageBeingEdited,
        persistedCheckoutState,
    })

    return {
        number_of_guests: initialNumberOfGuests,
        pricing_package_id: initialPricingPackageId,
        add_ons: initialAddOns,
    }
}

export function getEditableFields(
    bookingInvitePackageInfo?: BookingInvitePackageInfo | null
): BookingInviteEditableFields {
    const fields = bookingInvitePackageInfo?.editableFields

    // If fields are not defined, default to true.  This is because bookingInvite is not required on the
    // checkout page, so if it is not defined, we want to default to true.
    return {
        add_ons: fields?.add_ons ?? true,
        dates: fields?.dates ?? true,
        total_guests: fields?.total_guests ?? true,
        coupon: fields?.coupon ?? true,
        pricing_package: fields?.pricing_package ?? true,
    }
}

// Once a quote is created, we no longer need the dates step.
// If pricing package is prefilled, we don't need the step.
export function getStepConfigs({
    quote,
    pricingPackage,
    bookingInvitePackageInfo,
    quotePackageBeingEdited,
    isWidget,
}: GetStepConfigsFuncArgs) {
    const editableFields = bookingInvitePackageInfo?.editableFields
    const hasPricingPackage =
        Boolean(pricingPackage) ||
        Boolean(bookingInvitePackageInfo?.pricingPackage)
    const shouldIncludeDatesStep = !quote && editableFields?.dates !== false
    const shouldIncludePricingStep =
        (!hasPricingPackage && !quotePackageBeingEdited) ||
        Boolean(editableFields?.pricing_package)

    const shouldIncludeAccommodationsStep = getShouldIncludeAccomodationsStep({
        pricingPackage,
        bookingInvitePackageInfo,
    })
    const shouldShowWidgetPackageStep = isWidget && !quote

    const widgetPackageConfig = {
        label: COPY.EXPERIENCE,
        step: CHECKOUT_STEPS.WIDGET_PACKAGE,
    }
    const widgetInfoConfig = {
        label: COPY.INFO,
        step: CHECKOUT_STEPS.WIDGET_INFO,
    }
    const datesConfig = {
        label: COPY.DATES,
        step: CHECKOUT_STEPS.DATES,
    }
    const pricingPackageConfig = {
        label: COPY.PACKAGE,
        step: CHECKOUT_STEPS.PACKAGE,
    }

    const accommodationsStepConfig = {
        label: COPY.ACCOMMODATIONS,
        step: CHECKOUT_STEPS.ACCOMMODATIONS,
    }

    return [
        ...(shouldShowWidgetPackageStep ? [widgetPackageConfig] : []),
        ...(shouldIncludePricingStep ? [pricingPackageConfig] : []),
        ...(shouldIncludeDatesStep ? [datesConfig] : []),
        ...(shouldIncludeAccommodationsStep ? [accommodationsStepConfig] : []),
        {
            label: COPY.GUESTS_AND_ADD_ONS,
            step: CHECKOUT_STEPS.GUESTS_AND_ADD_ONS,
        },
        {
            label: COPY.SUMMARY,
            step: CHECKOUT_STEPS.SUMMARY,
        },
        ...(isWidget ? [widgetInfoConfig] : []),
        {
            label: COPY.PAYMENT,
            step: CHECKOUT_STEPS.PAYMENT,
        },
        {
            label: COPY.CONFIRMATION,
            step: CHECKOUT_STEPS.CONFIRMATION,
        },
    ]
}

export function findCurrentStepConfigIndex({
    currentStep,
    stepConfigs,
}: {
    currentStep: string | null
    stepConfigs: StepConfig[]
}) {
    return stepConfigs.findIndex(({ step }) => step === currentStep)
}

export function getInitialNumberOfGuests({
    pricingPackage,
    bookingInvitePackageInfo,
    quotePackageBeingEdited,
    persistedCheckoutState,
}: {
    pricingPackage?: PricingPackageFieldsFragment | null
    bookingInvitePackageInfo?: BookingInvitePackageInfo | null
    quotePackageBeingEdited?: QuotePackageFieldsFragment | null
    persistedCheckoutState?: CheckoutState | null
}) {
    if (persistedCheckoutState?.packageState.number_of_guests) {
        return persistedCheckoutState.packageState.number_of_guests
    }

    if (quotePackageBeingEdited) {
        return quotePackageBeingEdited.number_of_guests
    }

    if (bookingInvitePackageInfo) {
        return bookingInvitePackageInfo.totalGuests ?? 1
    }

    if (pricingPackage?.price_by_guest === PriceByGuest.SetGuest) {
        return pricingPackage.guests_included
    }

    return pricingPackage?.min_guests ?? 1
}

export function getQuotePackage(quote?: QuoteFieldsFragment | null) {
    const packageId = getQuotePackageIdUrlQueryParam()

    return quote?.packages.find((quotePackage) => quotePackage.id === packageId)
}

// Gets booking invite info from first package (for use with single package invites only).
// For backwards compatibility, this falls back to the top level pricing, guests, and add ons.
export function getBookingInviteInfoForSinglePackageInvite(
    bookingInvite: BookingInviteFieldsFragment | null | undefined
): BookingInvitePackageInfo | null {
    if (!bookingInvite) return null

    const invitePackage = bookingInvite.packages[0]

    return {
        id: bookingInvite.id,
        dates: bookingInvite.dates,
        coupon: bookingInvite.coupon,

        pricingPackage: invitePackage?.pricing_package,

        totalGuests: invitePackage?.total_guests,

        addOns: invitePackage?.add_ons,
        editableFields: getEditableFieldsFromBookingInviteAndPackage({
            bookingInvite,
            invitePackage,
        }),
    }
}

export function isMultiPackageBookingInvite(
    bookingInvite?: BookingInviteFieldsFragment
) {
    const packagesCount = bookingInvite?.packages.length ?? 0

    return packagesCount > 1
}

export function isCheckoutFlowEditable(
    bookingInvite: BookingInviteFieldsFragment | null | undefined
) {
    if (!bookingInvite) return true

    if (!bookingInvite.packages.length) {
        return isAnyEditableFieldsTrue(bookingInvite.editable_fields)
    }

    const hasAnyEditablePackages = bookingInvite.packages.some(
        (invitePackage) =>
            isAnyEditableFieldsTrue(invitePackage.editable_fields)
    )

    // We need to look at the top level editable_fields to see if dates are editable.
    return hasAnyEditablePackages || bookingInvite.editable_fields.dates
}

export function isAnyEditableFieldsTrue(
    editableFields: BookingInviteEditableFieldsFragment
) {
    // We shouldn't check coupon since it's not handled during checkout flow.
    // Entered at the final checkout/payment screen.
    const fieldsToSkip = ["__typename", "coupon"]

    return Object.entries(editableFields).some(
        ([key, isEditable]) => !fieldsToSkip.includes(key) && isEditable
    )
}

export function getListingTypeFromQuoteOrBookingInvite(
    quoteOrBookingInvite: QuoteFieldsFragment | BookingInviteFieldsFragment
) {
    return quoteOrBookingInvite.listing.type ?? ListingType.Hunting
}

// A valid dates array will have 1-2 items, start/end dates.
// For single date selections, we will format it to two.
export function areDatesValid(dates: Date[]) {
    return dates.length > 0
}

// Falls back to top level editable fields for backwards compatibility
// if there's no package.
function getEditableFieldsFromBookingInviteAndPackage({
    bookingInvite,
    invitePackage,
}: {
    bookingInvite: BookingInviteFieldsFragment
    invitePackage?: BookingInvitePackageFieldsFragment
}) {
    const inviteEditableFields = bookingInvite.editable_fields
    const packageEditableFields = invitePackage?.editable_fields

    return {
        // Should always come from top level editable fields.
        dates: inviteEditableFields.dates,
        coupon: inviteEditableFields.coupon,
        // The rest should be pulled from the package if available.
        pricing_package:
            packageEditableFields?.pricing_package ??
            inviteEditableFields.pricing_package,
        total_guests:
            packageEditableFields?.total_guests ??
            inviteEditableFields.total_guests,
        add_ons: packageEditableFields?.add_ons ?? inviteEditableFields.add_ons,
    }
}

function getInitialDates({
    bookingInvitePackageInfo,
    quote,
    persistedCheckoutState,
}: {
    bookingInvitePackageInfo?: BookingInvitePackageInfo | null
    quote?: QuoteFieldsFragment | null
    persistedCheckoutState: CheckoutState | null
}) {
    if (persistedCheckoutState?.dates) {
        return mapTimestampsToDates(persistedCheckoutState.dates)
    }

    const lastDate = quote?.dates[quote.dates.length - 1]
    if (quote?.dates[0] && lastDate) {
        return mapTimestampsToDates([quote.dates[0], lastDate])
    }

    return mapTimestampsToDates(bookingInvitePackageInfo?.dates ?? [])
}

function getInitialPricingPackageId({
    pricingPackage,
    bookingInvitePackageInfo,
    quotePackageBeingEdited,
    persistedCheckoutState,
}: {
    pricingPackage?: PricingPackageFieldsFragment | null
    bookingInvitePackageInfo?: BookingInvitePackageInfo | null
    quotePackageBeingEdited?: QuotePackageFieldsFragment | null
    persistedCheckoutState?: CheckoutState | null
}) {
    if (persistedCheckoutState?.packageState.pricing_package_id) {
        return persistedCheckoutState.packageState.pricing_package_id
    }
    if (quotePackageBeingEdited) {
        return quotePackageBeingEdited.pricing_package.id
    }

    return (
        bookingInvitePackageInfo?.pricingPackage?.id ??
        pricingPackage?.id ??
        null
    )
}

function getInitialAddOns({
    bookingInvitePackageInfo,
    quotePackageBeingEdited,
    persistedCheckoutState,
}: {
    bookingInvitePackageInfo?: BookingInvitePackageInfo | null
    quotePackageBeingEdited?: QuotePackageFieldsFragment | null
    persistedCheckoutState?: CheckoutState | null
}) {
    if (persistedCheckoutState?.packageState.add_ons) {
        return persistedCheckoutState.packageState.add_ons
    }

    if (!quotePackageBeingEdited) {
        return bookingInvitePackageInfo?.addOns ?? null
    }

    return quotePackageBeingEdited.add_ons.map((addOn) =>
        mapAddOnToAddOnInput({ addOn, quantity: addOn.quantity })
    )
}

export function mapTimestampsToDates(timestamps: number[]) {
    return timestamps.map((ts) => new Date(ts))
}

export function mapDatesToTimestamps(dates: Date[]) {
    return dates.map((date) => date.getTime())
}

function getShouldIncludeAccomodationsStep({
    pricingPackage,
    bookingInvitePackageInfo,
}: {
    pricingPackage?: PricingPackageFieldsFragment | null
    bookingInvitePackageInfo?: BookingInvitePackageInfo | null
}) {
    return (
        Boolean(pricingPackage?.included_lodging_id) ||
        Boolean(
            bookingInvitePackageInfo?.pricingPackage?.included_lodging_id
        ) ||
        Boolean(pricingPackage?.included_resource_ids?.length) ||
        Boolean(
            bookingInvitePackageInfo?.pricingPackage?.included_resource_ids
                ?.length
        )
    )
}
